import { ItemsIcon } from '@components/icons/Icons';
import { api, DateRange } from '@services/Api';
import { decode } from "html-entities";

export interface IssueFilters {
  date?: DateRange,
  multipleDateRanges?: DateRange[];
  showVariants?: boolean;
  onlyFirstIssues?: boolean;
  isHot?: boolean;
  isKey?: boolean;
  isRare?: boolean;
  query?: string;
  minimumScore?: number;
  publisherId?: number;
  publishers?: number[];

  series?: number[];
  sort?: number;
  groupByGrade?: boolean;
  locations?: string[];

  boxes?: number[];
  gradedBy?: string[];
  grades?: string[];
  formats?: string[];
  issueId?: number;
  itemInCollectionId?: string | number;
  variantOf?: number;
  showValuesShown?: number; // 0 = all, 1 = CovrPrice est value, 2 = user value.
}

export interface IssueFiltersQuery {
  userId: number,
  filters: IssueFilters,
}

export const getDateValueFromOption = (option) => {
  let [start, end] = option.value.split(',');
  let middle = option.label;
  return { start, middle, end }
}

export const getDateOptionsFirstVal = (data: Entry, pos = 0): DateSelect => {

  if (data.options) {
    const options = data.options;
    if (options && options.length > 0) {
      return getDateValueFromOption(options[pos]);
    }
  }

  return { start: null, middle: null, end: null };
}

export const filterAndSortData = (issues: any[], filters: any = {}) => {

  const { publishers, showVariants, justFirst, onlyShowUpdated, onlyShowNew, onlyShowPushed, showRegular } = filters;

  const filteredByPublisher = issues
    ?.filter((item: any) => publishers
      ?.includes(item.key));

  const filteredSortData = filteredByPublisher
    ?.map(issue => {
      return {
        key: issue.key,
        title: issue.title,
        items: issue.items
          ?.filter((item) => {

            if (justFirst && item.issue_number !== 1) {
              return false;
            }

            if (onlyShowUpdated && !onlyShowNew && !onlyShowPushed && item.updated !== true) {
              return false;
            }

            if (onlyShowNew && !onlyShowUpdated && !onlyShowPushed && item.new !== true) {
              return false;
            }

            if (onlyShowPushed && !onlyShowNew && !onlyShowUpdated && item.pushed !== true) {
              return false;
            }

            if (onlyShowNew && onlyShowUpdated && onlyShowPushed && (item.updated !== true && item.new !== true && item.pushed !== true)) {
              return false;
            }

            if ((!showVariants && item.variant_of > 0)) {
              return false;
            }

            if ((!showRegular &&
              !(item.variant_of && item.variant_of > 0))) {
              return false;
            }

            return true;

          })
      };
    });

  const filterOutPublisherNoItems = filteredSortData
    ?.filter((item: any) => item.items.length > 0)

  return filterOutPublisherNoItems;
}

const sortByTitle = (a, b) => {
  const titleA = a.title.toUpperCase();

  const titleB = b.title.toUpperCase();

  if (titleA < titleB) {
    return -1;
  } else if (titleA > titleB) {
    return 1;
  } else {
    return 0;
  }
};

const groupByAttribute = (arr, attribute) => {
  return Object.values(
    arr.reduce(
      (result, item) => {
        const key = item[attribute];
        const group = result[key] || {
          key: `${item.publisher_id}`,
          title: `${item.publisher_name}`,
          items: [],
        };
        group.items.push(item);
        result[key] = group;
        return result;
      }, {}
    )
  ).sort(sortByTitle);
};

export const transformIssueDataResponse = (data: any) => {

  const issues: any = data
    ?.issues.map((issue) => {
      return {
        ...issue,
        series_title: issue.series_title
          ? decode(issue.series_title)
          : null,
        summary: issue.summary
          ? decode(issue.summary)
          : null,
        custom_series_title: issue.custom_series_title
          ? decode(issue.custom_series_title)
          : null,
        custom_summary: issue.custom_summary
          ? decode(issue.custom_summary)
          : null,
        diamond_artist: issue.diamond_artist
          ? decode(issue.diamond_artist)
          : null,
        diamond_cover_artist: issue.diamond_cover_artist
          ? decode(issue.diamond_cover_artist)
          : null,
        diamond_writer: issue.diamond_writer
          ? decode(issue.diamond_writer)
          : null,
        variant_name: issue.variant_name
          ? decode(issue.variant_name)
          : null,
        publisher_name: issue.publisher_name
          ? decode(issue.publisher_name)
          : null,
        lunar_artist: issue.lunar_artist
          ? decode(issue.lunar_artist)
          : null,
        lunar_cover_artist: issue.lunar_cover_artist
          ? decode(issue.lunar_cover_artist)
          : null,
        lunar_writer: issue.lunar_writer
          ? decode(issue.lunar_writer)
          : null,
      }
    })

  const selectedIds: any = issues
    ?.filter((params) => params.excluded_from_export !== 1)
    ?.map((params) => params.post_id);

  const deselectedIds: any = issues
    ?.filter((params) => params.excluded_from_export === 1)
    ?.map((params) => params.post_id);

  const grpByPublisher: any = groupByAttribute(
    issues, 'publisher_name'
  );

  grpByPublisher.forEach(obj => {
    obj.items.sort((a, b) => {

      // First, sort by series_title
      const seriesTitleComparison = a.series_title.localeCompare(b.series_title);

      // If series_title is the same, then sort by issue_number
      if (seriesTitleComparison === 0) {
        const issueNumberComparison = a.issue_number - b.issue_number;

        // If issue_number is the same, then sort by variant_of 
        // (null comes first)
        return issueNumberComparison === 0
          ? (a.variant_of === null ? -1
            : (b.variant_of === null ? 1
              : a.variant_of - b.variant_of))
          : issueNumberComparison;
      }

      return seriesTitleComparison;
    });
  });

  const publishersOpitons: any = grpByPublisher
    ?.map((f) => {
      return {
        label: `${f.title} (${f.items.length})`,
        value: f.key
      }
    })

  const publisherIds: any = publishersOpitons
    ?.map(item => item?.value);

  const activeElements: any = data?.activeElements;

  return {
    ...data,
    grpByPublisher,
    publishersOpitons,
    publisherIds,
    selectedIds,
    deselectedIds,
    allIds: [
      ...selectedIds,
      ...deselectedIds
    ],
    activeElements,
  };
};

const issueApi = api.injectEndpoints({
  endpoints: (build) => ({
    getPartnerPortalNewReleaseIssues: build.query<any, any>({
      query: (args) => {
        const filters = args
          ? encodeURIComponent(btoa(JSON.stringify(args.filters)))
          : '';
        const userId = args
          ? encodeURIComponent(btoa(JSON.stringify(args.userId)))
          : '';
        return {
          url: `covr/v1/partner-portal/new-releases?userId=${userId}&filters=${filters}`,
          method: "GET",
        };
      },
      providesTags: ['PartnerPortal'],
      transformResponse: transformIssueDataResponse,
    }),
    getPartnerPortalFinalOrderCutOffIssues: build.query<any, any>({
      query: (args) => {
        const filters = args
          ? encodeURIComponent(btoa(JSON.stringify(args.filters)))
          : '';
        const userId = args
          ? encodeURIComponent(btoa(JSON.stringify(args.userId)))
          : '';
        return {
          url: `covr/v1/partner-portal/final-order-cutoffs?userId=${userId}&filters=${filters}`,
          method: "GET",
        };
      },
      providesTags: ['PartnerPortal'],
      transformResponse: transformIssueDataResponse,
    }),
  })
});

export const {
  useGetPartnerPortalNewReleaseIssuesQuery,
  useGetPartnerPortalFinalOrderCutOffIssuesQuery,
} = issueApi;
